import { useState } from 'react';
import { reminderCancel, reminderCreate, reminderSetStatus, type ApiClient, type ReminderKindName } from '@aithora/shared';
import { sx } from './designStyles';

// Reminders add / pause / resume / cancel, restored from the Flutter production console
// (widgets/reminders_card.dart; Aithora Home reminders). The design draws the list
// read-only; this is the smallest control under it, closed until opened. Every write goes
// to the served /reminders routes, which check who may touch this resident's reminders,
// refuse a medication-change instruction, and audit.

export interface ManagedReminder {
  id: string;
  title: string;
  kind: string;
  status: string;
}

const LINK = 'display:inline-flex;align-items:center;min-height:28px;padding:0 4px;font-size:14px;font-weight:600;text-decoration:none;color:#2F5C97;background:transparent;border:0;cursor:pointer;font-family:inherit;';
const INPUT = 'box-sizing:border-box;min-height:32px;padding:0 10px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-size:13px;color:#22272E;font-family:inherit;';
const KINDS: [ReminderKindName, string][] = [['checkin', 'Check-in'], ['appointment', 'Appointment'], ['medication', 'Medication'], ['custom', 'Custom']];

export function ReminderControls({ client, residentId, reminders, readOnly, readOnlyReason, onChanged, big = false }: {
  client: ApiClient;
  residentId: string;
  reminders: ManagedReminder[];
  readOnly: boolean;
  readOnlyReason?: string | null;
  onChanged: () => void;
  /** Aithora Home's larger type. */
  big?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<ReminderKindName>('checkin');
  const [time, setTime] = useState('09:00');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fs = big ? 'font-size:15px;' : '';

  const run = async (p: Promise<unknown>, done: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await p;
      setMsg(done);
      onChanged();
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      setMsg(/medication_change_blocked/.test(m) ? 'A reminder cannot tell anyone to change, skip or double a medication. Ask the care team.' : m);
    } finally {
      setBusy(false);
    }
  };
  const live = reminders.filter((r) => r.status === 'active' || r.status === 'paused');
  const tz = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';

  if (!open) {
    return (
      <button type="button" data-testid="reminders-manage" onClick={() => setOpen(true)} style={sx(LINK + fs + 'margin-top:6px;')}>
        Manage reminders
      </button>
    );
  }
  return (
    <div data-testid="reminders-controls" style={sx('display:flex;flex-direction:column;gap:6px;margin-top:8px;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);min-width:0;' + fs)}>
      {live.map((r) => (
        <div key={r.id} style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:4px;min-width:0;font-size:13px;' + fs)}>
          <span style={sx('flex:1 1 140px;min-width:0;color:#22272E;')}>{r.title}</span>
          <span style={sx('font-size:14px;color:#54606C;')}>{r.status === 'paused' ? 'paused' : 'on'}</span>
          <button type="button" disabled={readOnly || busy} title={readOnlyReason ?? undefined}
            onClick={() => void run(reminderSetStatus(client, { params: { id: r.id }, body: { residentId, status: r.status === 'paused' ? 'active' : 'paused' } }), r.status === 'paused' ? 'Resumed.' : 'Paused.')}
            style={sx(LINK + fs)}>
            {r.status === 'paused' ? 'Resume' : 'Pause'}
          </button>
          <button type="button" disabled={readOnly || busy} title={readOnlyReason ?? undefined}
            onClick={() => void run(reminderCancel(client, { params: { id: r.id }, query: { residentId } }), 'Cancelled. It will not fire again.')}
            style={sx(LINK + fs + 'color:#B3261E;')}>
            Cancel
          </button>
        </div>
      ))}
      {!live.length && <span style={sx('font-size:14px;color:#54606C;')}>No reminders set.</span>}
      <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;margin-top:4px;min-width:0;')}>
        <input aria-label="New reminder" placeholder="New reminder, for example Call Ruth" value={title} disabled={readOnly} onChange={(e) => setTitle(e.target.value)} style={sx(INPUT + 'flex:1 1 180px;min-width:0;' + fs)} />
        <select aria-label="Kind" value={kind} disabled={readOnly} onChange={(e) => setKind(e.target.value as ReminderKindName)} style={sx(INPUT + fs)}>
          {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input aria-label="Every day at" type="time" value={time} disabled={readOnly} onChange={(e) => setTime(e.target.value)} style={sx(INPUT + fs)} />
        <button type="button" disabled={readOnly || busy || !title.trim() || !/^\d{2}:\d{2}$/.test(time)} title={readOnlyReason ?? undefined}
          onClick={() => void run(reminderCreate(client, { body: { residentId, title: title.trim(), kind, recurrence: { freq: 'daily', times: [time] }, timezone: tz } }).then(() => setTitle('')), 'Added. It fires every day at ' + time + '.')}
          style={sx('display:inline-flex;align-items:center;min-height:32px;padding:0 12px;border-radius:7px;border:0;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;cursor:pointer;font-family:inherit;' + fs)}>
          Add
        </button>
        <button type="button" onClick={() => setOpen(false)} style={sx(LINK + fs + 'color:#54606C;')}>Close</button>
      </div>
      {msg && <span role="status" style={sx('font-size:14px;color:#2F5C97;')}>{msg}</span>}
    </div>
  );
}
